import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import z from "zod";

import { YesOrNoBadge } from "@/components/profiles";
import { QueryBuilderContext } from "@/contexts/query-builder-context";
import { useIntegrations } from "@/hooks/use-integrations";
import { useTableQueryBuilder } from "@/hooks/use-table-query-builder";
import type { PersonifyIdentity } from "@/types/integrations";
import { PaginatedTable } from "@/ui/common/paginated-table";
import { HeaderSearchInput } from "@/ui/table/header-search-input";
import { SearchHeaderSelect } from "@/ui/table/search-header-select";
import { formatShortDate, YES_OR_NO_OPTIONS } from "@/utils";

const columns: ColumnDef<PersonifyIdentity>[] = [
  {
    id: "id",
    accessorKey: "id",
  },
  {
    id: "externalMemberId",
    accessorKey: "externalMemberId",
    header: (header) => {
      return <HeaderSearchInput header={header} name="External Member ID" />;
    },
    enableSorting: false,
  },
  {
    id: "userEmail",
    accessorKey: "userEmail",
    header: "User Email",
    cell: ({ row }) => {
      return row.original.userEmail ?? row.original.emailAtLink ?? "—";
    },
    enableSorting: false,
  },
  {
    id: "eligibilityStatus",
    accessorKey: "eligibilityStatus",
    header: "Eligibility",
    cell: ({ row }) => {
      return row.original.eligibilityStatus ?? "—";
    },
    enableSorting: false,
  },
  {
    id: "hasUser",
    accessorKey: "hasUser",
    header: (header) => {
      return <SearchHeaderSelect header={header} name="Linked" options={YES_OR_NO_OPTIONS} />;
    },
    cell: ({ row }) => {
      return <YesOrNoBadge successValue={true} value={row.original.hasUser} />;
    },
    enableSorting: false,
  },
  {
    id: "linkedAt",
    accessorKey: "linkedAt",
    header: "Linked At",
    cell: ({ row }) => {
      return row.original.linkedAt ? formatShortDate(row.original.linkedAt) : "—";
    },
  },
  {
    id: "lastLoginAt",
    accessorKey: "lastLoginAt",
    header: "Last Login",
    cell: ({ row }) => {
      return row.original.lastLoginAt ? formatShortDate(row.original.lastLoginAt) : "—";
    },
  },
];

const PERSONIFY_IDENTITY_FILTER_KEYS = ["externalMemberId", "hasUser"] as const;

const PersonifyIdentitiesPage = () => {
  const { queryBuilder, setCurrentPage, setSorting, sortingState } = useTableQueryBuilder({
    filterKeys: PERSONIFY_IDENTITY_FILTER_KEYS,
  });

  const { data: identities, isLoading } = useIntegrations().getPersonifyIdentities({
    queryBuilder,
  });

  return (
    <QueryBuilderContext.Provider value={{ builder: queryBuilder }}>
      <PaginatedTable
        columns={columns}
        data={identities?.data}
        isLoadingData={isLoading}
        pagination={identities?.pagination}
        rowLink={(identity) => {
          return {
            to: "/integrations/personify/identities/$id",
            params: { id: identity.id },
          };
        }}
        setCurrentPage={setCurrentPage}
        setSorting={setSorting}
        sorting={sortingState}
      />
    </QueryBuilderContext.Provider>
  );
};

export const Route = createFileRoute("/_private/integrations/personify/identities/")({
  component: PersonifyIdentitiesPage,
  validateSearch: z.object({
    page: z.number().optional(),
    ...columns.reduce(
      (acc: Record<string, z.ZodOptional<z.ZodUnion<[z.ZodString, z.ZodNumber]>>>, column) => {
        if (column.id) {
          acc[column.id] = z.string().or(z.number().int()).optional();
        }

        return acc;
      },
      {},
    ),
  }),
});
